/**
 * BustaPagaSelector — twin dropdown (mese + anno) per il campo "Busta paga di recupero".
 *
 * Le buste paga totali sono 14:
 *   12 mesi standard (Gennaio…Dicembre) + Tredicesima (XIII) + Quattordicesima (XIV)
 *
 * Output value format (compatibile con backend `parse_payslip` in advances.py):
 *   "B.P. 01/2026" … "B.P. 12/2026" oppure "B.P. XIII/2026" / "B.P. XIV/2026"
 */
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";

const MONTHS = [
    { key: "01", label: "Gennaio" },
    { key: "02", label: "Febbraio" },
    { key: "03", label: "Marzo" },
    { key: "04", label: "Aprile" },
    { key: "05", label: "Maggio" },
    { key: "06", label: "Giugno" },
    { key: "07", label: "Luglio" },
    { key: "08", label: "Agosto" },
    { key: "09", label: "Settembre" },
    { key: "10", label: "Ottobre" },
    { key: "11", label: "Novembre" },
    { key: "12", label: "Dicembre" },
    { key: "XIII", label: "Tredicesima (XIII)" },
    { key: "XIV", label: "Quattordicesima (XIV)" },
];

/**
 * Parse a payslip string into { month, year }.
 *   "B.P. 04/2026"   → { month: "04",   year: "2026" }
 *   "B.P. XIII/2026" → { month: "XIII", year: "2026" }
 *   "" / invalid     → { month: "",     year: "" }
 */
export const parseBustaPaga = (value) => {
    if (!value || typeof value !== "string") return { month: "", year: "" };
    const cleaned = value.replace(/B\.P\./i, "").trim();
    const m = cleaned.match(/^(XIII|XIV|0?[1-9]|1[0-2])\s*\/\s*(\d{4})$/i);
    if (!m) return { month: "", year: "" };
    let month = m[1].toUpperCase();
    if (/^\d+$/.test(month)) month = month.padStart(2, "0");
    return { month, year: m[2] };
};

/** Format month + year into the canonical payslip string. */
export const formatBustaPaga = (month, year) => {
    if (!month || !year) return "";
    return `B.P. ${month}/${year}`;
};

/**
 * <BustaPagaSelector value onChange disabled testidPrefix="create" />
 *   value:         current payslip string (e.g. "B.P. 03/2026")
 *   onChange:      receives the new payslip string
 *   yearsRange:    [minYear, maxYear] (default: current year ±3)
 */
export const BustaPagaSelector = ({
    value,
    onChange,
    disabled = false,
    yearsRange,
    testidPrefix = "bp",
    compact = false,
}) => {
    const { month, year } = parseBustaPaga(value);
    const now = new Date().getFullYear();
    const [minY, maxY] = yearsRange || [now - 3, now + 3];
    const years = [];
    for (let y = maxY; y >= minY; y--) years.push(String(y));

    const emit = (nextMonth, nextYear) => {
        if (!nextMonth || !nextYear) {
            onChange("");
            return;
        }
        onChange(formatBustaPaga(nextMonth, nextYear));
    };

    return (
        <div className="grid grid-cols-2 gap-2">
            <div>
                {!compact && <Label className="text-[10px] uppercase tracking-wide text-slate-500">Busta paga</Label>}
                <Select
                    value={month}
                    onValueChange={(v) => emit(v, year || String(now))}
                    disabled={disabled}
                >
                    <SelectTrigger className={compact ? "h-8 text-xs" : "mt-1"} data-testid={`${testidPrefix}-payslip-month`}>
                        <SelectValue placeholder="Seleziona mese" />
                    </SelectTrigger>
                    <SelectContent>
                        {MONTHS.map((m) => (
                            <SelectItem key={m.key} value={m.key}>
                                {m.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div>
                {!compact && <Label className="text-[10px] uppercase tracking-wide text-slate-500">Anno</Label>}
                <Select
                    value={year}
                    onValueChange={(v) => emit(month, v)}
                    disabled={disabled}
                >
                    <SelectTrigger className={compact ? "h-8 text-xs" : "mt-1"} data-testid={`${testidPrefix}-payslip-year`}>
                        <SelectValue placeholder="Anno" />
                    </SelectTrigger>
                    <SelectContent>
                        {years.map((y) => (
                            <SelectItem key={y} value={y}>
                                {y}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};
